import { FC } from 'react';
import { Tooltip as UITooltip } from '@/components/ui';
import { Users, Calendar, Target, Clock, FileText, Activity, MousePointer, Eye } from 'lucide-react';
import { TabKey, MarketingKPIs, MxmLead, SxmBooking, ChartResponse } from '../types';
import { SkeletonKPICard } from './DashboardSkeletons';

export interface KPICardProps {
    label: string;
    value: string | number;
    icon: FC<{ className?: string }>;
    tooltip?: string;
}

export const KPICard: FC<KPICardProps> = ({ label, value, icon: Icon, tooltip }) => {
    const content = (
        <div className="bg-white rounded-xl p-5 border border-slate-100 hover:border-slate-200 transition-colors w-full h-full min-h-[150px]">
            <div className="p-2 bg-slate-50 rounded-lg w-fit">
                <Icon className="w-4 h-4 text-slate-500" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-3">{value}</p>
            <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mt-1">{label}</p>
        </div>
    );

    if (tooltip) {
        return (
            <UITooltip content={tooltip} position="top" className="w-full h-full">
                {content}
            </UITooltip>
        );
    }

    return content;
};

interface KPIGridProps {
    loading: boolean;
    activeTab: TabKey;
    leads: MxmLead[];
    bookings: SxmBooking[];
    marketingKPIs: MarketingKPIs | null;
    chartData: ChartResponse | null;
    conversionRate: number;
    pendingFollowUp: number;
}

export const KPIGrid: FC<KPIGridProps> = ({
    loading,
    activeTab,
    leads,
    bookings,
    marketingKPIs,
    chartData,
    conversionRate,
    pendingFollowUp
}) => {
    if (loading) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => <SkeletonKPICard key={i} />)}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {activeTab === 'sales' && (
                <>
                    <KPICard
                        label="Total Leads"
                        value={leads.length}
                        icon={Users}
                        tooltip="The total number of leads found through all channels."
                    />
                    <KPICard
                        label="Total Bookings"
                        value={bookings.length}
                        icon={Calendar}
                        tooltip="Total number of meetings that have been successfully booked."
                    />
                    <KPICard
                        label="Conversion Rate"
                        value={`${conversionRate.toFixed(1)}%`}
                        icon={Target}
                        tooltip="The percentage of your leads that have turned into booked meetings."
                    />
                    <KPICard
                        label="Pending Follow-up"
                        value={pendingFollowUp}
                        icon={Clock}
                        tooltip="Leads that are currently in progress and moving through your funnel."
                    />
                </>
            )}
            {activeTab === 'marketing' && (
                <>
                    <KPICard
                        label="Published"
                        value={marketingKPIs?.publishedContent || 0}
                        icon={FileText}
                        tooltip="Total number of posts that have been shared on your social media."
                    />
                    <KPICard
                        label="Scheduled"
                        value={marketingKPIs?.scheduledContent || 0}
                        icon={Calendar}
                        tooltip="Posts that are scheduled to be shared on your socials soon."
                    />
                    <KPICard
                        label="Engagement"
                        value={marketingKPIs?.totalEngagement?.toLocaleString() || '0'}
                        icon={Activity}
                        tooltip="Total number of times people have seen or interacted with your posts."
                    />
                    <KPICard
                        label="Avg CTR"
                        value={`${((marketingKPIs?.averageCTR || 0) * 100).toFixed(2)}%`}
                        icon={MousePointer}
                        tooltip="The average percentage of people who clicked on your posts after seeing them."
                    />
                </>
            )}
            {activeTab === 'topics' && (
                <>
                    <KPICard
                        label="Topics Analyzed"
                        value={chartData?.articlesRead || 0}
                        icon={Eye}
                        tooltip="The total number of different market topics we've analyzed for you."
                    />
                    <KPICard
                        label="Companies"
                        value={chartData?.companiesMonitored || 0}
                        icon={Users}
                        tooltip="The total number of unique companies we are monitoring for you."
                    />
                </>
            )}
        </div>
    );
};
